import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCamera, FaDownload, FaFileAlt, FaTimes } from 'react-icons/fa';
import { downloadCadernoEvidenceFile } from '../services/cadernoService';
import { formatEvidenceFileSize } from '../services/evidenceFileService';

export default function FieldNotebookAttachment({ attachment, onDownload }) {
  const [source, setSource] = useState('');
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const thumbnailRef = useRef(null);
  const closeRef = useRef(null);
  const isPhoto = attachment.type === 'foto';

  useEffect(() => {
    if (!isPhoto) return undefined;
    let active = true;
    let objectUrl;
    setSource('');
    setError(false);
    const load = async () => {
      try {
        let url;
        if (attachment.path) {
          const blob = await downloadCadernoEvidenceFile(attachment.path);
          if (!active) return;
          objectUrl = URL.createObjectURL(blob);
          url = objectUrl;
        } else if (attachment.legacyUrl) {
          const legacyUrl = new URL(attachment.legacyUrl, window.location.origin);
          if (!['http:', 'https:'].includes(legacyUrl.protocol)) throw new Error('URL inválida');
          url = legacyUrl.href;
        } else {
          throw new Error('Arquivo indisponível');
        }
        if (active) setSource(url);
      } catch {
        if (active) setError(true);
      }
    };
    load();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isPhoto, attachment.path, attachment.legacyUrl, attempt]);

  useEffect(() => {
    if (!expanded) return undefined;
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      thumbnailRef.current?.focus();
    };
  }, [expanded]);

  return (
    <div className={`field-task-attachment${isPhoto ? ' is-photo' : ''}`}>
      {isPhoto ? (
        <button
          ref={thumbnailRef}
          type="button"
          className="field-task-photo-thumbnail"
          aria-label={error ? `Tentar carregar ${attachment.name} novamente` : `Visualizar ${attachment.name}`}
          disabled={!source && !error}
          onClick={() => error ? setAttempt(current => current + 1) : setExpanded(true)}
        >
          {source && !error ? <img src={source} alt={attachment.name} onError={() => setError(true)} /> : <FaCamera />}
        </button>
      ) : <span><FaFileAlt /></span>}
      <div>
        <strong>{attachment.name}</strong>
        <small>{attachment.size > 0
          ? `${formatEvidenceFileSize(attachment.size)}${attachment.originalSize > attachment.size ? ` · compactado de ${formatEvidenceFileSize(attachment.originalSize)}` : ' · formato otimizado'}`
          : 'Arquivo original preservado'}</small>
        {isPhoto && !source && !error && <small role="status">Carregando foto...</small>}
        {error && <small role="alert">Foto indisponível. Toque para tentar novamente.</small>}
      </div>
      {(attachment.path || attachment.legacyUrl) && <button type="button" onClick={() => onDownload(attachment)} aria-label={`Baixar ${attachment.name}`} title="Baixar anexo"><FaDownload /></button>}
      {expanded && createPortal(
        <div className="field-photo-backdrop" onMouseDown={() => setExpanded(false)}>
          <div className="field-photo-viewer" role="dialog" aria-modal="true" aria-label={attachment.name} onMouseDown={event => event.stopPropagation()} onKeyDown={event => {
            if (event.key === 'Escape') setExpanded(false);
            if (event.key === 'Tab') {
              event.preventDefault();
              const buttons = Array.from(event.currentTarget.querySelectorAll('button'));
              const index = buttons.indexOf(document.activeElement);
              buttons[(index + (event.shiftKey ? buttons.length - 1 : 1)) % buttons.length]?.focus();
            }
          }}>
            <div className="field-photo-toolbar">
              <strong>{attachment.name}</strong>
              <button type="button" onClick={() => onDownload(attachment)} aria-label={`Baixar ${attachment.name}`} title="Baixar foto"><FaDownload /></button>
              <button ref={closeRef} type="button" onClick={() => setExpanded(false)} aria-label="Fechar foto" title="Fechar foto"><FaTimes /></button>
            </div>
            <img src={source} alt={attachment.name} />
          </div>
        </div>, document.body
      )}
    </div>
  );
}